import { useCallback, useState, useMemo, useEffect } from "react";
import { debounce } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { DIRECT_PATHS, SEARCH_PARAMS } from "constants/routes";
import { apiSearchProductCategory } from "services/api/helpers";
import { OptionType } from "types";
import { useQueryParam } from "helpers/search";

interface UseSearchLogicProps {
  initialFetch?: boolean;
  initialDelay?: number;
}

interface CategoryParams {
  categoryId?: string;
  countryId?: string;
}

export const useSearchLogic = ({ 
  initialFetch = false, 
  initialDelay = 2000 
}: UseSearchLogicProps = {}) => {
  const history = useHistory();
  const location = useLocation();
  const { categoryId } = useParams<CategoryParams>();
  const searchKeyWordParam = useQueryParam(SEARCH_PARAMS.keyword);
  
  const [selectedCategory, setSelectedCategory] = useState<OptionType | null>(null);
  const [searchKeyword, setSearchKeyword] = useState(
    decodeURIComponent(searchKeyWordParam || "")
  );
  const [searchCategories, setSearchCategories] = useState<any[]>([]);
  const [searchCategoryLoading, setSearchCategoryLoading] = useState(false);
  const [isUsingCategory, setIsUsingCategory] = useState(false);

  const isOnCategoryPage = useMemo(() => {
    const { pathname } = location;
    return pathname.includes('/buyer-category/') || pathname.includes('/supplier-category/');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const fetchOptions = useCallback((searchText: string | null) => {
    setSearchCategoryLoading(true);
    apiSearchProductCategory(searchText)
      .then((res) => {
        setSearchCategories(
          (res.data?.message?.data || []).map((c: any) => ({
            value: c.name,
            label: c.category_name,
          }))
        );
      })
      .finally(() => {
        setSearchCategoryLoading(false);
      });
  }, []);

  const delayedFetchOptions = useMemo(
    () => debounce(fetchOptions, 500),
    [fetchOptions]
  );

  const getButtonHref = useCallback((type: "supplier" | "buyer") => {
    const choosingCategory = isUsingCategory && selectedCategory;
    const encodeSearchKeyword = encodeURIComponent(searchKeyword);

    if (type === "supplier") {
      return choosingCategory
        ? DIRECT_PATHS.categorySupplier(selectedCategory.value, undefined)
        : DIRECT_PATHS.searchSupplier(encodeSearchKeyword);
    }
    if (type === "buyer") {
      return choosingCategory
        ? DIRECT_PATHS.categoryBuyer(selectedCategory.value)
        : DIRECT_PATHS.searchBuyer(encodeSearchKeyword);
    }
    return "/";
  }, [searchKeyword, selectedCategory, isUsingCategory]);

  const handleCategoryChange = useCallback((newValue: any) => {
    setSelectedCategory(newValue);
    if (newValue) {
      const finalSearchKeyword = newValue.label || newValue;
      setSearchKeyword(finalSearchKeyword);
      setIsUsingCategory(true);
    } else {
      setIsUsingCategory(false);
    }
  }, []);

  const handleInputChange = useCallback((newInputValue: string) => {
    // Nếu input value trống hoàn toàn, clear tất cả selections
    if (!newInputValue) {
      setSearchKeyword("");
      setSelectedCategory(null);
      setIsUsingCategory(false);
      delayedFetchOptions("");
      return;
    }
    
    // Nếu đang sử dụng category và input value khác với category label, clear category selection
    if (isUsingCategory && selectedCategory && newInputValue !== selectedCategory.label) {
      setSelectedCategory(null);
      setIsUsingCategory(false);
    }
    
    setSearchKeyword(newInputValue);
    delayedFetchOptions(newInputValue);
  }, [delayedFetchOptions, selectedCategory, isUsingCategory]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && searchKeyword?.length > 0) {
      e.preventDefault();
      const choosingCategory = isUsingCategory && selectedCategory;
      if (choosingCategory) {
        history.push(DIRECT_PATHS.categoryBuyer(selectedCategory.value));
      } else {
        history.push(DIRECT_PATHS.searchBuyer(encodeURIComponent(searchKeyword)));
      }
    }
  }, [searchKeyword, selectedCategory, history, isUsingCategory]);

  useEffect(() => {
    const keyword = decodeURIComponent(searchKeyWordParam || "");
    
    // Nếu đang ở category page, không update searchKeyword từ URL param
    // để giữ lại text của category đã chọn
    if (!isOnCategoryPage) {
      setSearchKeyword(keyword);
      setIsUsingCategory(false);
      setSelectedCategory(null);
    }
    
    if (keyword && !isOnCategoryPage) {
      delayedFetchOptions(keyword);
    }
  }, [searchKeyWordParam, delayedFetchOptions, isOnCategoryPage]);

  // Effect riêng để handle category từ URL params
  useEffect(() => {
    if (isOnCategoryPage && categoryId) {
      setIsUsingCategory(true);
      
      // Nếu đã có searchKeyword và nó không phải là categoryId thô, giữ nguyên
      if (searchKeyword && searchKeyword !== categoryId) {
        setSelectedCategory({
          value: categoryId,
          label: searchKeyword
        });
        return;
      }
      
      // Tìm category name từ searchCategories nếu có, nếu không thì dùng categoryId
      const categoryLabel = searchCategories.find(cat => cat.value === categoryId)?.label || categoryId;
      
      setSelectedCategory({
        value: categoryId,
        label: categoryLabel
      });
      
      // Chỉ update searchKeyword nếu nó đang trống hoặc khác với category label
      if (!searchKeyword || searchKeyword === categoryId) {
        setSearchKeyword(categoryLabel);
      }
    }
  }, [isOnCategoryPage, categoryId, searchCategories, searchKeyword]);

  useEffect(() => {
    if (initialFetch) {
      setTimeout(() => {
        fetchOptions(null);
      }, initialDelay);
    }
  }, [fetchOptions, initialFetch, initialDelay]);

  // Effect để fetch categories khi vào category page để có thể hiển thị tên category đúng
  useEffect(() => {
    if (isOnCategoryPage && categoryId && searchCategories.length === 0) {
      fetchOptions(null);
    }
  }, [isOnCategoryPage, categoryId, searchCategories.length, fetchOptions]);

  return {
    // State
    selectedCategory,
    searchKeyword,
    searchCategories,
    searchCategoryLoading,
    
    // Actions
    getButtonHref,
    handleCategoryChange,
    handleInputChange,
    handleKeyDown,
    
    // Utils
    setSelectedCategory,
    setSearchKeyword,
  };
}; 